import React from "react";
import { useVirtualRows } from "../../../hooks/useVirtualRows";
import { Head, PadRow, Row, Scroller, Spacer, Table } from "./VirtualListViewport.styles";
import type { VirtualListViewportProps } from "./VirtualListViewport.types";

/** A scroll area that renders only the rows in view. Stateless: the parent supplies the rows through `renderRow`. */
export const VirtualListViewport: React.FC<VirtualListViewportProps> = ({
  id,
  count,
  rowHeight,
  height = "100%",
  overscan,
  renderRow,
  layout = "list",
  tableHead,
  ariaLabel,
  empty,
  className,
}) => {
  const { scrollRef, items, totalSize } = useVirtualRows({ count, estimateSize: rowHeight, overscan });
  const first = items[0];
  const last = items[items.length - 1];
  const before = first ? first.start : 0;
  const after = last ? totalSize - (last.start + last.size) : 0;

  return (
    <Scroller id={id} ref={scrollRef} $height={height} className={className} aria-label={ariaLabel} tabIndex={0}>
      {count === 0 ? (
        empty
      ) : layout === "table" ? (
        <Table>
          {tableHead ? <Head>{tableHead}</Head> : null}
          <tbody>
            {before > 0 ? <PadRow aria-hidden="true" $size={before} data-testid={`${id}-pad-before`} /> : null}
            {items.map((row) => (
              <React.Fragment key={row.key}>{renderRow(row)}</React.Fragment>
            ))}
            {after > 0 ? <PadRow aria-hidden="true" $size={after} data-testid={`${id}-pad-after`} /> : null}
          </tbody>
        </Table>
      ) : (
        <Spacer $size={totalSize} data-testid={`${id}-spacer`}>
          {items.map((row) => (
            <Row key={row.key} $start={row.start} $size={row.size}>
              {renderRow(row)}
            </Row>
          ))}
        </Spacer>
      )}
    </Scroller>
  );
};

export default VirtualListViewport;
